import { ROUTES } from "@/routes_new";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { Trans, useLingui } from "@lingui/react/macro";
import { useEffect, useMemo, useState } from "react";
import { generatePath, useHistory, useParams } from "react-router-dom";
import { useSearchContext } from "./context";
import { Button } from "@components/ui/button";
import {
  Controller,
  SubmitHandler,
  useFormState,
  useWatch,
} from "react-hook-form";
import { useFormCompat } from "@/hooks/useFormCompat";
import { zodResolver } from "@hookform/resolvers/zod";
import { SearchSubmitForm, useSearchSubmitFormSchema } from "./partials/types";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@components/ui/field";
import { Input } from "@components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { SEARCH_TYPE_TO_LABEL, SearchType } from "@/constants";
import { i18n } from "@lingui/core";
import { Switch } from "@components/ui/switch";
import { Alert, AlertDescription, AlertTitle } from "@components/ui/alert";
import { InfoIcon, MessagesSquareIcon, TriangleAlertIcon } from "lucide-react";
import { format, isFuture, endOfYesterday } from "date-fns";
import { useI18n } from "@/contexts/i18nContext";
import { useMutation } from "@tanstack/react-query";
import { createSearch } from "@/api/endpoints/searches";
import { CandidateSearch } from "@/types/Search";
import { formatDateForApi } from "@/utils";
import { useDelayedLoading } from "@/hooks/useDelayedLoading";
import { Spinner } from "@components/ui/spinner";
import { toast } from "sonner";
import { Badge } from "@components/ui/badge";
import { useIntercom } from "@/hooks/useIntercom";

export default function SearchSubmit() {
  const { t } = useLingui();
  const { locale } = useI18n();
  const intercom = useIntercom();

  // URL state
  const history = useHistory();
  const { orgUuid } = useParams<{ orgUuid: string }>();

  // Local state
  const [isOpen, setIsOpen] = useState<boolean>(true);

  // Context
  const {
    candidateSearch,
    removeCandidateSearch,
    hasMediaMonitoringProduct,
    canCreateActiveSearch,
    maxNumAlertsCount,
    activeSearchesCount,
  } = useSearchContext();

  // Validation
  const searchSubmitFormSchema = useSearchSubmitFormSchema();

  // Form state
  const form = useFormCompat<SearchSubmitForm>({
    resolver: zodResolver(searchSubmitFormSchema),
    defaultValues: {
      title: candidateSearch?.title || "",
      start_date: candidateSearch?.start_date
        ? new Date(candidateSearch.start_date)
        : undefined,
      end_date: candidateSearch?.end_date
        ? new Date(candidateSearch.end_date)
        : null,
      type: undefined,
    },
  });

  const formState = useFormState({ control: form.control });

  const endDate = useWatch({ control: form.control, name: "end_date" });
  const startDate = useWatch({ control: form.control, name: "start_date" });

  const formattedEndDate = endDate
    ? format(endDate, "PPP", { locale })
    : undefined;

  const candidateEndDate = candidateSearch?.end_date
    ? new Date(candidateSearch.end_date)
    : null;
  const candidateEndDateIsFuture =
    candidateEndDate !== null && isFuture(candidateEndDate);

  // Server state
  const { mutateAsync: mutateCreateSearch } = useMutation({
    mutationFn: async (search: CandidateSearch) => {
      const result = await createSearch(search);
      if (!result.ok) throw new Error("createSearch failed");
      return result.data;
    },
    onSuccess: (created, variables) => {
      const targetRoute = variables.is_static
        ? ROUTES.SAVED_SEARCHES_DETAIL_ORGID
        : ROUTES.MENTION_DETAIL_ORGID;

      removeCandidateSearch();

      window.location.assign(
        generatePath(targetRoute, {
          orgUuid,
          uuid: created.uuid,
        })
      );
    },
    onError: () => {
      toast.error(t`Kunde inte skapa din bevakning`);
    },
  });

  const showSpinner = useDelayedLoading(formState.isSubmitting);

  // Handlers
  const handleSubmit: SubmitHandler<SearchSubmitForm> = async (values) => {
    if (!candidateSearch) return;

    const isSnapshot =
      values.end_date != null && !isFuture(new Date(values.end_date));

    return mutateCreateSearch({
      ...candidateSearch,
      title: values.title,
      search_type: values.type,
      start_date: formatDateForApi(values.start_date),
      end_date: values.end_date ? formatDateForApi(values.end_date) : null,
      is_static: isSnapshot,
    });
  };

  function handleOpenChange(open: boolean) {
    if (open || showSpinner.shouldRender) return;
    setIsOpen(false);
    setTimeout(() => {
      history.push(generatePath(ROUTES.SEARCH_PREVIEW_ORGID, { orgUuid }));
    }, 300);
  }

  // Helpers
  const inactiveSearchDateRange = useMemo(() => {
    return [startDate, endDate]
      .filter(Boolean)
      .map((date) => format(date, "PPP", { locale }))
      .join(" - ");
  }, [startDate, endDate, locale]);

  // Side-effects
  useEffect(() => {
    if (formState.dirtyFields.end_date) return;

    if (!hasMediaMonitoringProduct || !canCreateActiveSearch) {
      form.setValue("end_date", endOfYesterday());
    }
  }, [
    formState.dirtyFields.end_date,
    hasMediaMonitoringProduct,
    canCreateActiveSearch,
    form,
  ]);

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Trans>Lägg till information</Trans>
          </DialogTitle>
        </DialogHeader>

        <form
          id="search-submit-form"
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <FieldGroup className="gap-4">
            <Controller
              name="title"
              control={form.control}
              render={({ field, fieldState }) => {
                const id = "form-qb-title";

                return (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel className="sr-only" htmlFor={id}>
                      <Trans>Sökord</Trans>
                    </FieldLabel>

                    <Input
                      {...field}
                      aria-invalid={fieldState.invalid}
                      placeholder={t`Ge din sökning en beskrivande titel`}
                      autoComplete="off"
                      id={id}
                    />

                    {fieldState.invalid && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                );
              }}
            />

            <Controller
              name="type"
              control={form.control}
              render={({ field, fieldState }) => {
                const id = "form-qb-type";

                return (
                  <Field data-invalid={fieldState.invalid} className="flex-1">
                    <FieldLabel className="sr-only" htmlFor={id}>
                      <Trans>Söktyp</Trans>
                    </FieldLabel>

                    <Select
                      value={field.value}
                      onValueChange={(newType) => {
                        field.onChange(newType as SearchType);
                      }}
                    >
                      <SelectTrigger id={id} aria-invalid={fieldState.invalid}>
                        <SelectValue>
                          {(searchType: SearchType | null) =>
                            searchType
                              ? i18n._(SEARCH_TYPE_TO_LABEL[searchType])
                              : t`Välj typ av sökning`
                          }
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="w-(--anchor-width)">
                        {Object.entries(SEARCH_TYPE_TO_LABEL).map(
                          ([value, label]) => (
                            <SelectItem
                              data-testid={`form-qb-type-${value}`}
                              key={value}
                              value={value}
                            >
                              {i18n._(label)}
                            </SelectItem>
                          )
                        )}
                      </SelectContent>
                    </Select>

                    {fieldState.invalid && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                );
              }}
            />

            <Field orientation="horizontal">
              <FieldContent>
                <FieldLabel htmlFor="active">
                  <Trans>Aktiv</Trans>{" "}
                  <Badge variant="secondary">
                    {hasMediaMonitoringProduct ? (
                      <Trans>
                        {activeSearchesCount}/{maxNumAlertsCount} bevakningar
                      </Trans>
                    ) : (
                      <Trans>Inaktiverad</Trans>
                    )}
                  </Badge>
                </FieldLabel>

                {hasMediaMonitoringProduct && (
                  <FieldDescription>
                    <Trans>
                      Som standard lyssnar vi och uppdaterar med nya
                      omnämnanden.
                    </Trans>
                  </FieldDescription>
                )}
              </FieldContent>

              <Switch
                checked={endDate ? isFuture(endDate) : true}
                disabled={!hasMediaMonitoringProduct || !canCreateActiveSearch}
                onCheckedChange={(newChecked) => {
                  const nextEndDate = newChecked
                    ? candidateEndDateIsFuture
                      ? candidateEndDate
                      : null
                    : candidateEndDate && !candidateEndDateIsFuture
                      ? candidateEndDate
                      : endOfYesterday();
                  form.setValue("end_date", nextEndDate, {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
                id="active"
              />
            </Field>

            {endDate && (
              <Alert>
                {canCreateActiveSearch ? <InfoIcon /> : <TriangleAlertIcon />}

                {isFuture(endDate) ? (
                  <>
                    <AlertTitle>
                      <Trans>
                        Sökningen uppdateras inte efter {formattedEndDate}
                      </Trans>
                    </AlertTitle>

                    <AlertDescription>
                      <p>
                        <Trans>
                          Du kan ändra datumet i inställningarna för
                          bevakningen.
                        </Trans>
                      </p>
                    </AlertDescription>
                  </>
                ) : (
                  <>
                    <AlertTitle>
                      {canCreateActiveSearch || !hasMediaMonitoringProduct ? (
                        <Trans>Detta spårar inte nya omnämnanden</Trans>
                      ) : (
                        <Trans>Alla aktiva bevakningar är använda</Trans>
                      )}
                    </AlertTitle>

                    {(inactiveSearchDateRange ||
                      !canCreateActiveSearch ||
                      !hasMediaMonitoringProduct) && (
                      <AlertDescription>
                        <p>
                          {[
                            !hasMediaMonitoringProduct &&
                              t`Din plan inkluderar inte aktiva bevakningar.`,

                            inactiveSearchDateRange &&
                              t`Du sparar en ögonblicksbild från ${inactiveSearchDateRange}.`,

                            hasMediaMonitoringProduct &&
                              !canCreateActiveSearch &&
                              t`Kontakta oss för att uppgradera din plan och få fler aktiva bevakningar.`,
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        </p>
                      </AlertDescription>
                    )}
                  </>
                )}
              </Alert>
            )}
          </FieldGroup>
        </form>

        <DialogFooter>
          {hasMediaMonitoringProduct && canCreateActiveSearch ? (
            <DialogClose
              render={
                <Button variant="outline" disabled={showSpinner.shouldRender} />
              }
            >
              <Trans>Avbryt</Trans>
            </DialogClose>
          ) : intercom.show ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                intercom.show();
              }}
            >
              <Trans>Kontakta oss</Trans>
              <MessagesSquareIcon />
            </Button>
          ) : null}

          <Button
            type="submit"
            form="search-submit-form"
            disabled={showSpinner.shouldRender}
            data-testid="save-alert-button"
          >
            <Trans>Spara ändringar</Trans>
            {showSpinner.isVisible && <Spinner />}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
